import { useEffect, useRef, useState } from "react";
import type { PreflightVoice } from "../../hooks/usePreflightVoice";
import { TTS_PHRASES } from "../../audio/ttsPhrases";
import { PreflightStage } from "./PreflightStage";

// 遊び方チュートリアル (spec 2026-07-23 D1 / 柱A)。事前チェックの氏名確認の後、本編に入る前に
// 対話のルールを 3 ビートで優しく共有する。①AI が名乗る →②読み上げが終わったら自分の番
// →③話し終えたら少し待つ。
//
// 2026-07-25: ターン合図音 (SE) を全廃したため (実機で「耳が痛い」、spec D2 撤回)、②は
// 合図音の実演をやめ、「読み上げが終わったら、あなたの番」を**文言だけ**で教える形にした。
// ターン制を伝える目的 (US-3) 自体は残す。
//
// 送りは「ナレーション連動の自動送り」(spec D1、2026-07-24 更新)。読み上げが終わったら、
// 読み上げ後の小休止 + 最小表示時間を置いて自動で次へ進む (完了 step = D19 と同じ
// 「読み上げ → 最小表示 → 自動遷移」の作法)。ユーザーにボタン操作を求めず、wizard 全体を
// ハンズフリーで通す。
// 練習ターンは作らない (氏名入力が de-facto の練習を兼ねる)。
//
// 2026-08-04: **画面を本編の前半と同じ「マイクだけ」に統一した** (spec 2026-08-04 D14)。
// それまでは読み上げている文をそのまま画面にも出していたが、これは**この案内自身が教えて
// いること (「読み上げが終わったら、あなたの番」= 耳で聞いて答える) と正面から矛盾する** —
// 目で読めるなら聞く必要が無く、被験者はここで「画面を読む」やり方を覚えて本編に入る。
// マイクを出すのは、本編でこれから見る画面をそのまま見せながら説明できるため
// (「マイクが開きます」が、そのマイクを見ながら耳に入る)。
const BEATS: string[] = [
  TTS_PHRASES.tutorialBeat1,
  TTS_PHRASES.tutorialBeat2,
  TTS_PHRASES.tutorialBeat3,
];

// ナレーション連動の自動送りの間 (spec D1)。読み上げ後にこれだけ置いてから次のビートへ。
// - MIN_ON_SCREEN_MS: 床。voice.speak は合成失敗でも即 resolve する (原則5) ため、この床が
//   無いと一瞬で全ビートを飛ばしてしまう。**画面から文字を消した後もこの床は要る** —
//   読む時間の確保ではなく、合成が全滅したときに案内が一瞬で通り過ぎるのを防ぐため。
// - POST_NARRATION_MS: 読み上げが実際に鳴った場合の「読み上げ後の小休止」(音が鳴り終わった
//   直後に飛ばず、一呼吸置く)。読み上げが数秒鳴れば床は既に満たされ、こちらが効く。
const MIN_ON_SCREEN_MS = 2500;
const POST_NARRATION_MS = 700;

type Props = {
  voice: PreflightVoice;
  onDone: () => void; // 最後のビートの後に ready へ
};

export function Tutorial({ voice, onDone }: Props) {
  const [index, setIndex] = useState(0);
  const isLast = index === BEATS.length - 1;
  // 最新値を effect / handler から読むための ref (usePreflightVoice / NameCapture と同じ作法)。
  // deps に入れず ref 経由で読むことで、ナレーション effect の deps を [index, voice] に保ち、
  // 親の再レンダーで読み上げ + 自動送りが再起動する事故を防ぐ。
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  // 1 ビートにつき 1 回だけ進めるためのガード (二重送り / ビート飛ばし防止)。最後のビートは
  // onDone がインデックスを変えないため、二重に発火すると onDone が 2 回呼ばれてしまう
  // (親が tutorial_viewed を二重 emit / setStep 二重)。ここで 1 回に固定する。
  // 2026-08-04 に送りボタンを畳んだので、いま呼ぶのは自動送りタイマーの 1 経路だけになり、
  // 到達可能な二重送りは無い (unmount / ビート切替との競合は cancelled が先に止める)。
  // **観測差が無い = 外しても落ちるテストが無い**が、送りの経路を足す人のための番人として残す。
  const advancedRef = useRef(false);
  // advance 本体は毎レンダー最新の isLast / onDone を閉じ込めて ref に載せ替える。effect からは
  // advanceRef.current() で呼び、effect の deps を [index, voice] に保つ (上記コメント参照)。
  const advanceRef = useRef<() => void>(() => undefined);
  advanceRef.current = () => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    voice.stopSpeaking();
    if (isLast) onDoneRef.current();
    else setIndex((i) => i + 1);
  };

  // ビートが変わるたびに読み上げ、ナレーション連動の間 (最小表示時間 + 小休止) を置いて
  // 自動で次のビートへ進める。voice.speak は失敗しても resolve する (原則5) ので、音が
  // 鳴らなくても進行は止まらない。beat は effect 内で index から導出する (deps を
  // [index, voice] に保つため)。
  useEffect(() => {
    advancedRef.current = false; // 新しいビート = 送りを 1 回だけ許可する
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const beat = BEATS[index];
    const shownAt = performance.now();
    void (async () => {
      await voice.speak(beat);
      if (cancelled) return;
      const wait = Math.max(POST_NARRATION_MS, MIN_ON_SCREEN_MS - (performance.now() - shownAt));
      await new Promise<void>((resolve) => {
        timer = setTimeout(resolve, wait);
      });
      if (cancelled) return;
      advanceRef.current();
    })();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      voice.stopSpeaking();
    };
  }, [index, voice]);

  // 画面は本編の前半とまったく同じ「マイク 1 つ」(spec 2026-08-04 D14)。完了案内 (D15) と
  // 同じ PreflightStage を共有しているので、この 2 step の境目でも本編に入る瞬間でも
  // 画面は 1 ドットも動かない (見え方の根拠は PreflightStage 側のコメント)。
  return <PreflightStage testId="tutorial" />;
}
